import React, { MouseEvent, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import styled, { useTheme } from 'styled-components';
import { Tooltip } from 'app-components';

import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { IMember } from '../../types/customGroupingTypes';
import { helperFunctions } from '@utils/tools/helperFunctions';
import { ConditionalRendering } from '@components/common/conditional-rendering';
import { GICheckbox, GIFormControl, GIFormLabel } from '@components/common/app-components/GIInput';
import { BREAK_POINTS } from '@constants/common/breakpoints';
import { useAppDispatch } from '@hooks/index';
import useCustomGrouping from '../../hooks/useCustomGrouping';
import CapitalizedButton from './CapitalizedButton';

const StyledMemberCardWrapper = styled.div`
    display: grid;
    grid-template-columns: 1fr 2fr;
    grid-template-rows: auto auto;
    border: ${({ theme, isBorderVisible }) => (isBorderVisible ? `1px solid ${theme.gray8}` : 'none')};
    border-radius: 4px;
    padding: 5px 0px 5px 15px;
    background-color: white;

    @media (max-width: ${BREAK_POINTS.sm}) {
        grid-template-columns: 1fr;
        grid-template-rows: auto;
        padding: 2px 0px 2px 5px;
    }
`;

const StyledMembersContainer = styled.div`
    grid-column: 1;
    grid-row: 1;

    .gi-checkbox {
        margin-bottom: 0;
        margin-top: 0;
    }

    .gi-checkbox__label {
        margin-bottom: 0;
    }

    @media (max-width: ${BREAK_POINTS.sm}) {
        grid-column: 1;
    }
`;

const StyledPlanDetailsContainer = styled.div`
    grid-column: 2 / span 2;
    grid-row: 1;
    min-width: 300px;

    @media (max-width: ${BREAK_POINTS.sm}) {
        grid-column: 1;
        grid-row: 2;
        min-width: 100%;
    }
`;

const StyledCTAContainer = styled.div`
    grid-column: 2 / span 2;
    grid-row: 2;

    @media (max-width: ${BREAK_POINTS.sm}) {
        grid-column: 1;
        grid-row: 3;
    }
`;

export interface IMemberCardConfig {
    data: IMember;
    config: {
        isCheckBoxVisible?: boolean;
        isChecked?: boolean;
        isCheckBoxDisabled?: boolean;
        tooltipTitle?: string;
        tooltipBody?: string;
        tooltipValues?: Record<string, any>;
    };
}

interface ICtaButton {
    label: string;
    labelValues?: {
        [propName: string]: any;
    };
    onClick: Function;
    isDisabled?: ({
        primaryMembersState,
        secondaryMembersState,
    }: {
        primaryMembersState: IMemberCardConfig[];
        secondaryMembersState: IMemberCardConfig[];
    }) => boolean;
}

interface IMemberCardProps {
    id: string;
    primaryMembers?: IMemberCardConfig[];
    secondaryMembers?: IMemberCardConfig[];
    planDetails?: {
        headers?: {
            header?: string;
            headerValues?: Record<string, any>;
        }[];
        footers?: {
            footer?: string;
            footerValues?: Record<string, any>;
        }[];
        issuerName?: string;
        planName?: string;
        netPremium?: string;
        perimoumAmount?: string;
    };
    primaryCTA?: ICtaButton;
    secondaryCTA?: ICtaButton;
    isBorderVisible?: boolean;
    onMemberChecked?: Function;
    /** ID of the element that provides group context for screen readers (e.g., "Shop for these members") */
    groupLabelId?: string;
}

const MemberCard = (props: IMemberCardProps) => {
    const {
        id,
        primaryMembers = [],
        secondaryMembers = [],
        planDetails,
        primaryCTA,
        secondaryCTA,
        isBorderVisible,
        onMemberChecked,
        groupLabelId,
    } = props;
    const [primaryMembersState, setPrimaryMembers] = useState(primaryMembers);
    const [secondaryMembersState, setSecondaryMembers] = useState(secondaryMembers);
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const { getSelectedCount } = useCustomGrouping({ dispatch });

    function primaryCTAOnClick(e: MouseEvent<HTMLButtonElement>) {
        e.preventDefault();
        primaryCTA?.onClick({ primaryMembers: primaryMembersState, secondaryMembers: secondaryMembersState });
    }

    function secondaryCTAOnClick(e: MouseEvent<HTMLButtonElement>) {
        e.preventDefault();
        secondaryCTA?.onClick({ primaryMembers: primaryMembersState, secondaryMembers: secondaryMembersState });
    }

    function onCheckboxChange(index: number, isPrimary: boolean) {
        const setState = isPrimary ? setPrimaryMembers : setSecondaryMembers;
        setState(prev => {
            const isCheck = !prev[index].config.isChecked;
            const newState = [
                ...prev.slice(0, index),
                {
                    ...prev[index],
                    config: {
                        ...prev[index].config,
                        isChecked: isCheck,
                    },
                },
                ...prev.slice(index + 1),
            ];

            if (
                onMemberChecked &&
                !onMemberChecked?.({
                    secondaryMembers: newState,
                    setMembersState: setState,
                    isCheck,
                    member: prev[index],
                })
            )
                return prev;
            return newState;
        });
    }

    return (
        <>
            <StyledMemberCardWrapper isBorderVisible={isBorderVisible}>
                <StyledMembersContainer data-testid="members_container">
                    {primaryMembersState.map(({ data, config }, index) => (
                        <div className="member-spacing" key={`primary_member_${id}_${data.id}`}>
                            <ConditionalRendering isTrue={config.isCheckBoxVisible}>
                                <GIFormControl
                                    id="primaryMembersForm"
                                    name={`primary_member_${id}_${data.id}`}
                                    type="checkbox"
                                >
                                    <GICheckbox
                                        id={`primary_member_${id}_${data.id}`}
                                        checked={config.isChecked}
                                        onChange={() => onCheckboxChange(index, true)}
                                        name={`primary_member_${id}_${data.id}`}
                                        disabled={config.isCheckBoxDisabled}
                                        groupLabelId={groupLabelId}
                                    />
                                    <GIFormLabel
                                        name={`primary_member_${id}_${data.id}`}
                                        id={`primary_member_${id}_${data.id}`}
                                        className="member-info bold-text d-inline"
                                    >
                                        {helperFunctions.getMemberName(data)}
                                    </GIFormLabel>
                                    <ConditionalRendering isTrue={!!config.tooltipTitle}>
                                        <Tooltip id={config.tooltipTitle!} content={config.tooltipBody!} />
                                    </ConditionalRendering>
                                </GIFormControl>
                            </ConditionalRendering>
                            <ConditionalRendering isTrue={!config.isCheckBoxVisible}>
                                <FontAwesomeIcon
                                    icon={faCheck}
                                    color={theme.checkedIconColor}
                                    data-testid={`primary_member_checked_${id}_${data.id}`}
                                    className="bold-text"
                                />
                                <span
                                    className="ml-3 bold-text member-info"
                                    data-testid={`primary_member_label_${id}_${data.id}`}
                                >
                                    {helperFunctions.getMemberName(data)}
                                </span>
                                <ConditionalRendering isTrue={!!config.tooltipTitle}>
                                    <Tooltip
                                        id={config.tooltipTitle!}
                                        content={config.tooltipBody!}
                                        type="light"
                                        plain={false}
                                        contentReplacement={config.tooltipValues!}
                                    />
                                </ConditionalRendering>
                            </ConditionalRendering>
                        </div>
                    ))}

                    {secondaryMembersState.map(({ data, config }, index) => (
                        <div className="member-spacing" key={`secondary_member_${id}_${data.id}`}>
                            <ConditionalRendering isTrue={config.isCheckBoxVisible}>
                                <GIFormControl
                                    id="secondaryMembersForm"
                                    name={`secondary_member_${id}_${data.id}`}
                                    type="checkbox"
                                >
                                    <GICheckbox
                                        id={`secondary_member_${id}_${data.id}`}
                                        checked={config.isChecked}
                                        onChange={() => onCheckboxChange(index, false)}
                                        name={`secondary_member_${id}_${data.id}`}
                                        disabled={config.isCheckBoxDisabled}
                                        groupLabelId={groupLabelId}
                                    />
                                    <GIFormLabel
                                        name={`secondary_member_${id}_${data.id}`}
                                        id={`secondary_member_${id}_${data.id}`}
                                        className="member-info bold-text d-inline"
                                    >
                                        {helperFunctions.getMemberName(data)}
                                    </GIFormLabel>
                                    <ConditionalRendering isTrue={!!config.tooltipTitle}>
                                        <Tooltip
                                            id={config.tooltipTitle!}
                                            content={config.tooltipBody!}
                                            type="light"
                                        />
                                    </ConditionalRendering>
                                </GIFormControl>
                            </ConditionalRendering>
                            <ConditionalRendering isTrue={!config.isCheckBoxVisible}>
                                <FontAwesomeIcon
                                    icon={faCheck}
                                    color={theme.successDark}
                                    data-testid={`secondary_member_checked_${id}_${data.id}`}
                                />
                                <span
                                    className="ml-3 bold-text member-info bold-text"
                                    data-testid={`secondary_member_label_${id}_${data.id}`}
                                >
                                    {helperFunctions.getMemberName(data)}
                                </span>
                                <ConditionalRendering isTrue={!!config.tooltipTitle}>
                                    <Tooltip
                                        id={config.tooltipTitle!}
                                        content={config.tooltipBody!}
                                        type="light"
                                        plain={false}
                                        contentReplacement={config.tooltipValues!}
                                    />
                                </ConditionalRendering>
                            </ConditionalRendering>
                        </div>
                    ))}
                </StyledMembersContainer>

                <StyledPlanDetailsContainer data-testid="plan_details">
                    {planDetails?.headers?.map(header => (
                        <ConditionalRendering isTrue={!!header.header} key={`plan_header_${header.header}`}>
                            <div data-testid={`plan_header_${header.header}`}>
                                <FormattedMessage
                                    id={header.header}
                                    values={header.headerValues}
                                />{' '}
                            </div>
                        </ConditionalRendering>
                    ))}
                    <div className="row-fluid margin10-b margin5-l my-2">
                        <div data-testid="plan_issuer"> {planDetails?.issuerName} </div>
                        <div data-testid="plan_name"> {planDetails?.planName} </div>
                        <ConditionalRendering isTrue={!!planDetails?.netPremium}>
                            <div className="bold-text">
                                <span data-testid="plan_net_premium">
                                    <FormattedMessage
                                        id={planDetails?.netPremium}
                                        values={{ amount: planDetails?.perimoumAmount }}
                                    />
                                </span>
                            </div>
                        </ConditionalRendering>
                    </div>
                    {planDetails?.footers?.map(footer => (
                        <ConditionalRendering isTrue={!!footer.footer} key={`plan_footer_${footer.footer}`}>
                            <div data-testid={`plan_footer_${footer.footer}`}>
                                <FormattedMessage
                                    id={footer.footer}
                                    values={footer.footerValues}
                                />{' '}
                            </div>
                        </ConditionalRendering>
                    ))}
                </StyledPlanDetailsContainer>
                <StyledCTAContainer>
                    <div className="row-fluid mt-2">
                        <ConditionalRendering isTrue={!!secondaryCTA}>
                            <CapitalizedButton
                                id={`secondary_CTA_${id}`}
                                labelId={secondaryCTA?.label}
                                labelValue={{
                                    ...secondaryCTA?.labelValues,
                                    primarySelections: getSelectedCount(primaryMembersState),
                                    secondarySelections: getSelectedCount(secondaryMembersState),
                                }}
                                variant="secondary"
                                onClickHandler={secondaryCTAOnClick}
                                addlClassNames="pull-left bold-button"
                                isDisabled={secondaryCTA?.isDisabled?.({
                                    primaryMembersState,
                                    secondaryMembersState,
                                })}
                            />
                        </ConditionalRendering>
                        <ConditionalRendering isTrue={!!primaryCTA}>
                            <CapitalizedButton
                                id={`primary_CTA_${id}`}
                                labelId={primaryCTA?.label}
                                labelValue={{
                                    ...primaryCTA?.labelValues,
                                    primarySelections: getSelectedCount(primaryMembersState),
                                    secondarySelections: getSelectedCount(secondaryMembersState),
                                }}
                                variant="primary"
                                onClickHandler={primaryCTAOnClick}
                                addlClassNames="pull-right bold-button"
                                isDisabled={primaryCTA?.isDisabled?.({
                                    primaryMembersState,
                                    secondaryMembersState,
                                })}
                            />
                        </ConditionalRendering>
                    </div>
                </StyledCTAContainer>
            </StyledMemberCardWrapper>
        </>
    );
};

export default MemberCard;
